import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { ArrowLeft, Share2, Twitter, Facebook, Linkedin, Link2, Check, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HonoreeDetailPage = () => {
  const { id } = useParams();
  const [honoree, setHonoree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchHonoree = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/honorees/${id}`);
        setHonoree(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching honoree:', err);
        if (err.response?.status === 404) {
          setError('Honoree not found');
        } else {
          setError('Failed to load honoree. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchHonoree();
  }, [id]);

  // Share functionality
  const getShareUrl = () => {
    return window.location.href;
  };

  const getShareText = () => {
    if (!honoree) return '';
    return `🖖 Congratulations to ${honoree.name} for receiving the LLAP Tribute Award from the Nimoy-Knight Foundation! #LLAP #RememberingLeonardNimoy`;
  };

  const shareToTwitter = () => {
    const text = getShareText();
    const url = getShareUrl();
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareToFacebook = () => {
    const url = getShareUrl();
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(getShareText())}`, '_blank');
  };

  const shareToLinkedIn = () => {
    const url = getShareUrl();
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b]">
        <Navbar />
        <div className="flex justify-center items-center py-40">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          <span className="ml-3 text-gray-400">Loading honoree...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0b]">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-40 px-4">
          <div className="text-6xl mb-6">🖖</div>
          <h1 className="text-2xl font-bold text-white mb-4">{error}</h1>
          <p className="text-gray-400 mb-8">The honoree you're looking for may have been moved or doesn't exist.</p>
          <Link
            to="/honorees"
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            View All Honorees
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      {/* Dynamic Open Graph Meta Tags */}
      <Helmet>
        <title>{honoree.name} | LLAP Tribute Award Honoree</title>
        <meta name="description" content={honoree.quote ? `${honoree.quote.substring(0, 155)}...` : `${honoree.name} has been recognized with the LLAP Tribute Award for their outstanding contributions to the community.`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={`${honoree.name} | LLAP Tribute Award Honoree`} />
        <meta property="og:description" content={honoree.quote ? `"${honoree.quote.substring(0, 150)}..."` : `Congratulations to ${honoree.name} for receiving the LLAP Tribute Award from the Nimoy-Knight Foundation.`} />
        <meta property="og:image" content={honoree.image} />
        <meta property="og:url" content={getShareUrl()} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${honoree.name} | LLAP Tribute Award Honoree`} />
        <meta name="twitter:description" content={honoree.quote ? `"${honoree.quote.substring(0, 150)}..."` : `Congratulations to ${honoree.name} for receiving the LLAP Tribute Award from the Nimoy-Knight Foundation.`} />
        <meta name="twitter:image" content={honoree.image} />
      </Helmet>

      <Navbar />
      
      {/* Back Button */}
      <div className="pt-24 pb-4 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/honorees"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          data-testid="back-to-honorees"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to All Honorees
        </Link>
      </div>

      {/* Honoree Profile */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#141416] border border-gray-800/50 rounded-3xl overflow-hidden">
            {/* Header with Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-b from-amber-500/10 via-transparent to-transparent" />
              <div className="relative pt-12 pb-8 px-8 flex flex-col md:flex-row items-center gap-8">
                {/* Profile Image */}
                <div className="w-40 h-40 md:w-48 md:h-48 rounded-2xl overflow-hidden flex-shrink-0 border-4 border-amber-500/30 shadow-2xl">
                  <img
                    src={honoree.image}
                    alt={`${honoree.name} - LLAP Award Honoree`}
                    className="w-full h-full object-cover"
                    loading="eager"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%231a1a1c" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%23666" font-size="40">🖖</text></svg>';
                    }}
                  />
                </div>
                
                {/* Info */}
                <div className="text-center md:text-left">
                  <span className="inline-block px-3 py-1 bg-amber-500/10 text-amber-500 text-sm font-semibold rounded-full mb-3">
                    LLAP Tribute Award Honoree
                  </span>
                  <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" data-testid="honoree-name">
                    {honoree.name}
                  </h1>
                  <p className="text-gray-400 text-lg">{honoree.title}</p>
                  {honoree.organization && (
                    <p className="text-amber-500 text-lg font-medium mt-1">{honoree.organization}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Quote Section */}
            <div className="px-8 pb-8">
              {honoree.quote ? (
                <div className="bg-[#1a1a1c] rounded-2xl p-8 border border-gray-800/50">
                  <div className="text-amber-500 text-5xl font-serif leading-none mb-4">"</div>
                  <blockquote className="text-gray-300 text-lg leading-relaxed italic" data-testid="honoree-quote">
                    {honoree.quote}
                  </blockquote>
                  <div className="text-amber-500 text-5xl font-serif leading-none text-right mt-4">"</div>
                </div>
              ) : (
                <div className="bg-[#1a1a1c] rounded-2xl p-8 border border-gray-800/50 text-center">
                  <div className="text-4xl mb-4">🖖</div>
                  <p className="text-gray-400">
                    {honoree.name} has been recognized for their outstanding contributions to the community and their embodiment of Leonard Nimoy's message of hope, logic, and compassion.
                  </p>
                </div>
              )}
            </div>

            {/* Share Section */}
            <div className="px-8 pb-8">
              <div className="bg-[#0f0f12] rounded-2xl p-6 border border-gray-800/50">
                <div className="flex items-center gap-2 mb-4">
                  <Share2 className="w-5 h-5 text-amber-500" />
                  <span className="text-white font-semibold">Share this honoree</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={shareToTwitter}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 border border-[#1DA1F2]/30 rounded-full text-[#1DA1F2] font-medium transition-all"
                    aria-label="Share on Twitter"
                    data-testid="share-twitter-btn"
                  >
                    <Twitter className="w-5 h-5" />
                    Twitter
                  </button>
                  <button
                    onClick={shareToFacebook}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#1877F2]/10 hover:bg-[#1877F2]/20 border border-[#1877F2]/30 rounded-full text-[#1877F2] font-medium transition-all"
                    aria-label="Share on Facebook"
                    data-testid="share-facebook-btn"
                  >
                    <Facebook className="w-5 h-5" />
                    Facebook
                  </button>
                  <button
                    onClick={shareToLinkedIn}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 border border-[#0A66C2]/30 rounded-full text-[#0A66C2] font-medium transition-all"
                    aria-label="Share on LinkedIn"
                    data-testid="share-linkedin-btn"
                  >
                    <Linkedin className="w-5 h-5" />
                    LinkedIn
                  </button>
                  <button
                    onClick={copyLink}
                    className={`flex items-center gap-2 px-5 py-2.5 border rounded-full font-medium transition-all ${
                      copied 
                        ? 'bg-green-500/10 border-green-500/30 text-green-500' 
                        : 'bg-gray-800/50 hover:bg-gray-800 border-gray-700 text-gray-300'
                    }`}
                    aria-label="Copy link"
                    data-testid="share-copy-link-btn"
                  >
                    {copied ? (
                      <>
                        <Check className="w-5 h-5" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Link2 className="w-5 h-5" />
                        Copy Link
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LLAP Section */}
      <section className="py-16 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-5xl mb-6">🖖</div>
          <h2 className="text-2xl font-bold text-white mb-4">
            Live Long and Prosper
          </h2>
          <p className="text-gray-400 leading-relaxed mb-8">
            The LLAP Tribute Award honors individuals who embody Leonard Nimoy's message of hope, logic, compassion, and service to others.
          </p>
          <Link
            to="/honorees"
            className="inline-flex items-center justify-center px-8 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all hover:scale-105"
          >
            View All Honorees
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HonoreeDetailPage;
